<template>
  <div>
    <portal :to="destination">
      <transition name="modal">
        <div
          ref="overlay"

          v-show="visible"
          class="modal modal-mask"
          :class="{ '-no-scrolllock': !scrollLock && visible, '-fixed': fixed }"
        >
          <div
            class="modal-wrapper grid"
            :class="{
              '-fullscreen-mobile': fullscreenMobile,
              '-vertical-center': verticalCenter,
            }"
            v-if="visible"
            @click.self="onClickOutSide"
          >
            <div
              role="dialog"
              aria-modal="true"
              :aria-label="title"
              class="modal-container g-12 -p-relative"
              :class="[
                `g-lg-${(12 - gridCols) / 2 + 1}-${gridCols}`,
                { '-transparent': transparent },
              ]"
            >
              <div
                class="modal-head"
                :class="{ '-transparent': transparent || noHeader }"
              >
                <span v-if="showBack" @click.stop="$emit('back')" class="back">
                  <Chevron
                    direction="left"
                    :size="$mqAliases.tabletOrLarger ? 18 : 16"
                  ></Chevron>
                  <span class="apple-4">{{
                    insertString("Global", "Back")
                  }}</span>
                </span>
                <span v-else class="font-responsive-body">{{ title }}</span>
                <div class="modal-close" tabindex="0" role="button" aria-label="close modal dialog" @click="close" @keydown.enter="close">
                  <SvgComponent
                    :icon="{ name: 'Close' }"
                    @click.native="close"
                    v-if="closeButton"
                  />
                </div>
                <hr />
              </div>
              <div
                ref="modalContent"
                class="modal-content -p-relative"
                :class="{
                  '-no-bottom-padding': noBottomPadding || compact,
                  '-no-padding': noPadding,
                  '-transparent': transparent,
                }"
              >
                <slot></slot>
              </div>
            </div>
            <div class="height-eater" @click.self="onClickOutSide"></div>
          </div>
        </div>
      </transition>
    </portal>
  </div>
</template>

<script>
import SvgComponent from '@/Scripts/components/shared/svg/Svg.vue';
import ScrollLock from '@/Scripts/lib/mixins/scrollLock.js';
import Chevron from '@/Scripts/components/shared/svg/Chevron/Chevron.vue';

function trapFocus (e, modalId) {
  const isTabPressed = e.key === 'Tab' || e.keyCode === 9;

  if (!isTabPressed) {
    return;
  }
  const focusableElements = 'button, [href], input, select, textarea, iframe, [tabindex]:not([tabindex="-1"])';
  const modal = document.getElementById(modalId);

  // get focusable elements in modal
  const firstFocusableElement = modal.querySelectorAll(focusableElements)[0];
  const focusableContent = modal.querySelectorAll(focusableElements);
  const lastFocusableElement = focusableContent[focusableContent.length - 1];

  if (e.shiftKey) {
    if (document.activeElement === firstFocusableElement) {
      lastFocusableElement.focus();
      e.preventDefault();
    }
  } else if (document.activeElement === lastFocusableElement) {
    firstFocusableElement.focus();
    e.preventDefault();
  }
}

function initTrapFocus (e) {
  return trapFocus(e, 'modalPortalWrapper');
}

/**
 * Start listening for tabs by adding an event listener when the modal opens.
 */
function openModalFocus () {
  document.addEventListener('keydown', initTrapFocus);
  document.body.querySelectorAll(':scope > div, :scope > header, :scope > footer, :scope > ul, :scope > section, :scope > a').forEach((el) => {
    if (el !== document.getElementById('modalPortalWrapper')) {
      el.setAttribute('aria-hidden', 'true');
    }
  });
  document.getElementById('modalPortalWrapper').setAttribute('aria-hidden', 'false');
}

/**
 * Stop listening for tabs by removing the event listener when the modal closes.
 */
function closeModalFocus () {
  document.removeEventListener('keydown', initTrapFocus);
  document.body.querySelectorAll(':scope > [aria-hidden="true"]').forEach((el) => {
    el.setAttribute('aria-hidden', 'false');
  });
  document.getElementById('modalPortalWrapper').setAttribute('aria-hidden', 'true');
}

export default {

  mixins: [ScrollLock],
  components: {
    SvgComponent,
    Chevron
  },
  props: {
    /*  visiblility of modal. Use v-model to control visibility */
    visible: {
      type: Boolean,
      default: false
    },
    altDestination: String,
    /* if modal should be closed on click outside */
    clickOutside: {
      type: Boolean,
      default: true
    },
    /* if modal should disable scroll on page */
    scrollLock: {
      type: Boolean,
      default: true
    },
    /* if modal should have close button */
    closeButton: {
      type: Boolean,
      default: true
    },
    gridCols: {
      type: Number,
      default: 8
    },
    fullscreenMobile: {
      type: Boolean,
      default: false
    },

    showBack: {
      type: Boolean,
      default: false
    },
    title: String,
    scrollClose: {
      type: Boolean,
      default: false
    },
    compact: Boolean,
    noBottomPadding: Boolean,
    noPadding: Boolean,
    verticalCenter: Boolean,
    transparent: Boolean,
    noHeader: Boolean,
    fixed: Boolean
  },
  model: {
    prop: 'visible',
    event: 'change'
  },
  data () {
    return {
      loaded: false,
      loadedDelay: 330,
      scrollPos: 0,
      includeWindowY: true
    };
  },
  watch: {
    visible: function (n) {
      if (n) {
        this.open();
      } else {
        this.close();
      }
    }
  },
  computed: {
    destination () {
      if (this.altDestination) {
        return this.altDestination;
      }
      return 'modal-destination';
    }
  },
  methods: {
    open () {
      openModalFocus();
      if (this.scrollLock) {
        this.lockScroll();
      } else {
        this.freeScroll();
      }
      setTimeout(() => {
        this.loaded = true;

        const focusableElements = this.$refs.modalContent.querySelectorAll('button, p, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
        const firstElement = focusableElements[0];
        if (firstElement) {
          firstElement.focus();
        }

        if (!this.scrollLock) {
          const height = Math.max(document.body.scrollHeight, document.body.offsetHeight, document.documentElement.clientHeight, document.documentElement.scrollHeight, document.documentElement.offsetHeight);
          if (document.getElementsByClassName('-no-scrolllock')[0]) {
            document.getElementsByClassName('-no-scrolllock')[0].style.height = `${height}px`;
          }
        }
      }, this.loadedDelay);
    },
    close () {
      closeModalFocus();
      if (this.scrollLock) {
        this.freeScroll();
      }
      this.loaded = false;
      this.$emit('close');
    },
    onClickOutSide () {
      if (this.loaded && this.clickOutside) {
        this.close();
      }
    }
  },
  destroyed () {
    if (this.visible) {
      this.freeScroll();
    }
  },
  mounted () {
    if (this.visible) {
      this.$nextTick(() => {
        this.open();
      });
    }
  }
};
</script>
<style lang="scss">
@import "@/scss/defaults.scss";

.modal {
  @include setNewFont();
  h2 {
    @include font-responsive(larger);
  }
  &-mask {
    position: absolute;

    z-index: 99998;
    top: 0;
    left: 0;
    width: 100%;
    height: 100vh;
    background-color: rgba(0, 0, 0, 0.5);
    display: table;
  }
  &-wrapper {
    height: 100%;
    padding-top: 9px;
    padding-bottom: 9px;
    grid-template-rows: max-content 1fr;
    @include media-breakpoint-down(sm) {
      &.-fullscreen-mobile {
        padding: 0;

        .modal-container {
          max-height: 100vh;
        }
      }
    }

    @include media-breakpoint-up(md) {
      padding-top: 60px;
      padding-bottom: 60px;
    }

    @include media-breakpoint-up(lg) {
      padding-top: 120px;
      padding-bottom: 120px;
    }
    &.-vertical-center {
      padding-top: 9px;
      padding-bottom: 9px;
      grid-template-rows: 1fr max-content 1fr;
      .modal-container {
        grid-row: 2;
      }
    }
  }

  &-head {
    height: 60px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid transparent;
    position: sticky;
    top: 0;
    background: $white;
    z-index: 1;
    @include make-padding-x();
    &.-transparent {
      position: absolute;
      width: 100%;
      background-color: transparent;
      hr {
        display: none;
      }
    }

    hr {
      border: 0;
      background: $gray-light;
      height: 1px;
      position: absolute;
      left: 0;
      right: 0;
      top: 60px;
      @include make-negative-margin-x();
      margin-top: 0;
      margin-bottom: 0;
    }
  }

  &-container {
    display: flex;
    flex-direction: column;
    position: relative;
    overflow-y: hidden;
    overflow-x: hidden;
    width: unset;
    max-width: unset;
    margin: 0;
    grid-template-rows: 60px 1fr;

    background-color: $white;
    -ms-overflow-style: none;
    &.-transparent {
      background-color: transparent;
    }
    @include pretty-scrollbars;

    p:last-child {
      margin-bottom: 0;
    }

    @include media-breakpoint-up(lg) {
      @include make-negative-margin-x();
    }

    max-height: calc(100vh - 18px);
    min-height: 180px;
    @include media-breakpoint-up(md) {
      max-height: calc(100vh - 120px);
    }
    @include media-breakpoint-up(lg) {
      max-height: calc(100vh - 240px);
    }
  }
  .modal-wrapper.-vertical-center {
    .modal-container {
      max-height: calc(100vh - 18px);
    }
  }
  &.-no-scrolllock {
    .modal-container {
      @include media-breakpoint-up(md) {
        max-height: none !important;
      }
    }
  }
  &.-fixed {
    position: fixed;
  }

  &-content {
    flex-grow: 1;
    @include make-padding-x();
    overflow-y: auto;
    overflow-x: hidden;
    @include pretty-scrollbars;

    padding-bottom: 60px;
    @include media-breakpoint-up(md) {
      max-height: calc(100vh - 78px);
      padding-bottom: 80px;
    }
    &.-no-bottom-padding {
      padding-bottom: 0;
    }
    &.-no-padding {
      padding: 0;
    }
  }
  hr {
    border: 0;
    background: $gray-light;
    height: 1px;
  }

  &-close {
    cursor: pointer;
    padding: 18px;
    margin-right: -18px;
  }

  .back {
    cursor: pointer;
    display: flex;
    align-items: center;

    span {
      margin-left: 6px;
    }
  }
}

.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.33s;
  opacity: 1;
  .modal-container {
    opacity: 1;
    transform: translateY(0);
    transition: all 0.33s;
  }
}

.modal-enter,
.modal-leave-to {
  opacity: 0;
  .modal-container {
    opacity: 0;
    transform: translateY(-50px);
  }
}
</style>
